import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Modal,
  Checkbox,
  Input,
  Button,
  Collapse,
  Typography,
  Space,
  Divider,
  Row,
  Col,
  Card,
  Badge,
  Empty,
  Spin,
} from "antd";
import { SearchOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "Store/hooks";
import {
  getAllFeatureGroups,
} from "../redux/thunks";
import {
  selectFeatureGroupList,
  selectIsFeatureGroupLoading,
} from "../redux/selectors";
import { IFeatureGroup } from "../redux/types";

const { Search } = Input;
const { Panel } = Collapse;
const { Text, Title } = Typography;

interface FeatureSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedFeatureGroups: string[]) => void;
  initialSelectedFeatures?: string[];
  title?: string;
}

interface GroupedFeatures {
  [category: string]: IFeatureGroup[];
}

const FeatureSelectionModal: React.FC<FeatureSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialSelectedFeatures = [],
  title = "Select Feature Groups",
}) => {
  const dispatch = useAppDispatch();
  const featureGroups = useAppSelector(selectFeatureGroupList);
  const isLoading = useAppSelector(selectIsFeatureGroupLoading);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(
    initialSelectedFeatures
  );
  const [groupedFeatures, setGroupedFeatures] = useState<GroupedFeatures>({});
  const hasLoadedRef = useRef(false);
  const lastInitialFeaturesRef = useRef<string[]>([]);

  const groupFeaturesByCategory = useCallback(() => {
    const filtered = featureGroups.filter(
      (group) =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (group.description &&
          group.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const grouped: GroupedFeatures = {};

    filtered.forEach((group) => {
      // Determine category based on feature group name patterns
      let category = "General";

      if (group.name.includes("Connector") || group.name.includes("Database")) {
        category = "Data Connectors";
      } else if (
        group.name.includes("Transformation") ||
        group.name.includes("Dataflow")
      ) {
        category = "Data Processing";
      } else if (
        group.name.includes("AI") ||
        group.name.includes("DataScience")
      ) {
        category = "AI & Analytics";
      } else if (
        group.name.includes("Cloud") ||
        group.name.includes("Portal")
      ) {
        category = "Cloud & Platform";
      } else if (
        group.name.includes("Workflow") ||
        group.name.includes("Scheduler")
      ) {
        category = "Automation";
      } else if (group.name.includes("Team") || group.name.includes("User")) {
        category = "Collaboration";
      } else if (group.name.includes("Docspire")) {
        category = "Docspire";
      }

      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(group);
    });

    setGroupedFeatures(grouped);
  }, [featureGroups, searchTerm]);

  // Load feature groups only once when modal opens (if not already loaded)
  useEffect(() => {
    if (isOpen && !hasLoadedRef.current) {
      // Only fetch if feature groups are not already loaded
      if (!featureGroups || featureGroups.length === 0) {
        dispatch(getAllFeatureGroups());
      }
      hasLoadedRef.current = true;
    }
    if (!isOpen) {
      hasLoadedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dispatch]); // featureGroups intentionally excluded to prevent re-runs

  // Update selected features when modal opens (only once per open)
  useEffect(() => {
    if (isOpen) {
      // Only update if initialSelectedFeatures has actually changed
      const currentInitial = JSON.stringify(initialSelectedFeatures || []);
      const lastInitial = JSON.stringify(lastInitialFeaturesRef.current);
      
      if (currentInitial !== lastInitial) {
        setSelectedFeatures(initialSelectedFeatures || []);
        lastInitialFeaturesRef.current = initialSelectedFeatures || [];
      }
    } else {
      // Reset when modal closes
      lastInitialFeaturesRef.current = [];
    }
  }, [isOpen, initialSelectedFeatures]);

  useEffect(() => {
    if (featureGroups?.length > 0) {
      groupFeaturesByCategory();
    }
  }, [featureGroups, searchTerm, groupFeaturesByCategory]);

  const handleFeatureToggle = (featureId: string, checked: boolean) => {
    if (checked) {
      setSelectedFeatures([...selectedFeatures, featureId]);
    } else {
      setSelectedFeatures(selectedFeatures.filter((id) => id !== featureId));
    }
  };

  const handleSelectAll = (category: string, checked: boolean) => {
    const categoryFeatures = groupedFeatures[category] || [];
    const categoryIds = categoryFeatures.map((f) => f.id);

    if (checked) {
      const newSelected = [
        ...selectedFeatures,
        ...categoryIds.filter((id) => !selectedFeatures.includes(id)),
      ];
      setSelectedFeatures(newSelected);
    } else {
      setSelectedFeatures(
        selectedFeatures.filter((id) => !categoryIds.includes(id))
      );
    }
  };

  const handleSelectNone = () => {
    setSelectedFeatures([]);
  };

  const handleSelectAllFeatures = () => {
    const allIds = featureGroups.map(f => f.id);
    setSelectedFeatures(allIds);
  };

  const isCategoryFullySelected = (category: string) => {
    const categoryFeatures = groupedFeatures[category] || [];
    return (
      categoryFeatures?.length > 0 &&
      categoryFeatures?.every((f) => selectedFeatures.includes(f.id))
    );
  };

  const isCategoryPartiallySelected = (category: string) => {
    const categoryFeatures = groupedFeatures[category] || [];
    const selectedInCategory = categoryFeatures.filter((f) =>
      selectedFeatures.includes(f.id)
    )?.length;
    return (
      selectedInCategory > 0 && selectedInCategory < categoryFeatures?.length
    );
  };

  const handleConfirm = () => {
    onConfirm(selectedFeatures);
    onClose();
  };

  const getSelectedFeatureNames = () => {
    return selectedFeatures
      .map(
        (id) =>
          featureGroups.find((f) => f.id === id)?.displayName ||
          featureGroups.find((f) => f.id === id)?.name
      )
      .filter(Boolean);
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CheckCircleOutlined />
          {title}
          {selectedFeatures?.length > 0 && (
            <Badge
              count={selectedFeatures?.length}
              style={{ backgroundColor: "#52c41a" }}
            />
          )}
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      width={800}
      style={{ top: 20 }}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="select-none" onClick={handleSelectNone}>
          Select None
        </Button>,
         <Button
           key="select-all"
           onClick={handleSelectAllFeatures}
         >
           Select All
         </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          Confirm Selection ({selectedFeatures?.length})
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Search feature groups..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          prefix={<SearchOutlined />}
          allowClear
        />
      </div>

      {isLoading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading feature groups...</div>
        </div>
      ) : Object.keys(groupedFeatures).length === 0 ? (
        <Empty
          description="No feature groups found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Collapse
          defaultActiveKey={Object.keys(groupedFeatures)}
          ghost
          expandIconPosition="right"
        >
          {Object.entries(groupedFeatures).map(([category, features]) => (
            <Panel
              key={category}
              header={
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Checkbox
                    checked={isCategoryFullySelected(category)}
                    indeterminate={isCategoryPartiallySelected(category)}
                    onChange={(e) =>
                      handleSelectAll(category, e.target.checked)
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Title level={5} style={{ margin: 0 }}>
                    {category} ({features.length})
                  </Title>
                </div>
              }
            >
              <Row gutter={[16, 16]}>
                {features?.map((feature) => (
                  <Col xs={24} sm={12} md={8} key={feature.id}>
                    <Card
                      size="small"
                      hoverable
                      style={{
                        border: selectedFeatures.includes(feature.id)
                          ? "2px solid #52c41a"
                          : "1px solid #d9d9d9",
                        backgroundColor: selectedFeatures.includes(feature.id)
                          ? "#f6ffed"
                          : "white",
                        padding: 10,
                        height: "100%"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                        }}
                      >
                        <Checkbox
                          checked={selectedFeatures.includes(feature.id)}
                          onChange={(e) =>
                            handleFeatureToggle(feature.id, e.target.checked)
                          }
                        />
                        <div style={{ flex: 1 }}>
                          <Text strong>
                            {feature.displayName || feature.name}
                          </Text>
                          {feature.description && (
                            <div>
                              <Text
                                type="secondary"
                                style={{ fontSize: "12px" }}
                              >
                                {feature.description}
                              </Text>
                            </div>
                          )}
                          <div>
                            <Text code style={{ fontSize: "11px" }}>
                              {feature.name}
                            </Text>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Panel>
          ))}
        </Collapse>
      )}

      {selectedFeatures?.length > 0 && (
        <>
          <Divider />
          <div>
            <Title level={5}>
              Selected Features ({selectedFeatures?.length}):
            </Title>
            <div style={{ maxHeight: "120px", overflowY: "auto" }}>
              <Space wrap>
                {getSelectedFeatureNames()?.map((name, index) => (
                  <Badge
                    key={index}
                    count={name}
                    style={{ backgroundColor: "#52c41a" }}
                  />
                ))}
              </Space>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default FeatureSelectionModal;
